import React, { useEffect, useState } from 'react';
import '../style/home.scss';
import '../style/privacy-policy.scss';
import '../style/user_management.scss';
import { googleServer, localServer } from "../commons/commonVariable";
import useSessionRedirect from "../commons/commonHook";
import { User } from "../models/User";

const UserManagement = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
    const [sortField, setSortField] = useState<keyof User>('firstName');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');


    // useSessionRedirect();

    useEffect(() => {
        const fetchUsers = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${googleServer}/user/getUsers`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setUsers(data);
                } else if (response.status === 401) {
                    console.warn("Unauthorized access");
                    window.location.href = 'https://aemr4all.com/';
                } else {
                    console.warn("Unexpected error:", response.status);
                }
            } catch (error) {
                console.error("Failed to fetch users:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUsers();
    }, []);

    const handleSort = (field: keyof User) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedUsers(new Set(users.map((_, index) => index)));
        } else {
            setSelectedUsers(new Set());
        }
    };

    const handleSelectUser = (index: number, checked: boolean) => {
        const newSelected = new Set(selectedUsers);
        if (checked) {
            newSelected.add(index);
        } else {
            newSelected.delete(index);
        }
        setSelectedUsers(newSelected);
    };

    const handleReset = async (user: User) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${googleServer}/user/resetExportCount`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: user.id,
                })
            });
        } catch (error) {
            console.error("Failed to fetch users:", error);
            window.location.href = 'https://aemr4all.com/';
        } finally {
            setIsLoading(false);
        }
    };
    const handleDelete = async (userId: string) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${googleServer}/user/delete/${userId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
        } catch (error) {
            console.error("Failed to fetch users:", error);
            window.location.href = 'https://aemr4all.com/';

        } finally {
            setIsLoading(false);
        }
    };


    const sortedUsers = [...users].sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortDirection === 'asc' 
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortDirection === 'asc' 
                ? aValue - bValue
                : bValue - aValue;
        }
        
        return 0;
    });

    return (
        <div className="user-management-container">
            <div className="header-section">
                <h1 className="main-heading">User Management</h1>
                <p className="description">
                    Manage all users in one place. Control access, assign roles, and monitor activity across your platform.
                </p>
            </div>
            <div className="table-container">
                <table className="user-table">
                    <thead>
                        <tr>
                            <th className="checkbox-header">
                                <input
                                    type="checkbox"
                                    checked={selectedUsers.size === users.length && users.length > 0}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                />
                            </th>
                            <th 
                                className="sortable-header"
                                onClick={() => handleSort('firstName')}
                            >
                                Full Name
                                <span className="sort-icon">
                                    {sortField === 'firstName' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                                </span>
                            </th>
                            <th 
                                className="sortable-header"
                                onClick={() => handleSort('email')}
                            >
                                Email
                                <span className="sort-icon">
                                    {sortField === 'email' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                                </span>
                            </th>
                            <th 
                                className="sortable-header"
                                onClick={() => handleSort('institution')}
                            >
                                Institution
                                <span className="sort-icon">
                                    {sortField === 'institution' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                                </span>
                            </th>
                            <th 
                                className="sortable-header"
                                onClick={() => handleSort('department')}
                            >
                                Department
                                <span className="sort-icon">
                                    {sortField === 'department' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                                </span>
                            </th>
                            <th 
                                className="sortable-header"
                                onClick={() => handleSort('exportCount')}
                            >
                                Export Count
                                <span className="sort-icon">
                                    {sortField === 'exportCount' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                                </span>
                            </th>
                            <th className="actions-header">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={7} className="loading-cell">
                                    <div className="loading-spinner">Loading...</div>
                                </td>
                            </tr>
                        ) : sortedUsers.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="no-data-cell">
                                    No users found.
                                </td>
                            </tr>
                        ) : (
                            sortedUsers.map((user, index) => (
                                <tr key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                    <td className="checkbox-cell">
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.has(index)}
                                            onChange={(e) => handleSelectUser(index, e.target.checked)}
                                        />
                                    </td>
                                    <td className="name-cell">
                                        <div className="user-info">
                                            <span className="full-name">
                                                {user.firstName} {user.lastName}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="email-cell">{user.email}</td>
                                    <td className="institution-cell">{user.institution}</td>
                                    <td className="department-cell">{user.department}</td>
                                    <td className="export-count-cell">{user.exportCount}</td>
                                    <td className="actions-cell">
                                        <button
                                            className="action-btn delete-btn"
                                            onClick={() => handleReset(user)}
                                            title="Initialize"
                                        >
                                            🔄
                                        </button>
                                        <button
                                            className="action-btn delete-btn"
                                            onClick={() => handleDelete(user.id)}
                                            title="Delete"
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UserManagement;